import { Injectable, OnDestroy, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject, fromEvent, startWith, take, takeUntil, map, Subject } from 'rxjs';

import { ApWindowService } from '@ap/cdk';

@Injectable({
    providedIn: 'root'
})
export class ApDarkmodeService implements OnDestroy {
    private readonly windowService = inject(ApWindowService);
    private readonly document = inject<Document>(DOCUMENT);

    /** darkmodeSetting holds the current appearance setting and can be used in other modules */
    private readonly darkmodeSetting$: BehaviorSubject<'light' | 'dark'> = new BehaviorSubject<'light' | 'dark'>('light');

    private readonly destroy$ = new Subject<void>();

    /**
     * Angular lifecycle method. Clears subscriptions
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Check `(prefers-color-scheme: dark` media query for automatic darkmode setting
     */
    public initializeAutomaticDarkmode() {
        const mediaQuery = this.windowService.getWindow().matchMedia('(prefers-color-scheme: dark)');
        fromEvent<MediaQueryList>(mediaQuery, 'change')
            .pipe(
                startWith(mediaQuery),
                map((list) => (list.matches ? 'dark' : 'light')),
                takeUntil(this.destroy$)
            )
            .subscribe((mode: 'dark' | 'light') => this.setDarkmode(mode));
    }

    /**
     * Set dark mode to `dark` or `light`.
     * Adds appropriate class to .ap wrappers on the page
     */
    public setDarkmode(setting: 'dark' | 'light') {
        if (this.document.readyState === 'loading') {
            fromEvent(window, 'DOMContentLoaded')
                .pipe(take(1))
                .subscribe(() => this.addClass(setting));
        } else {
            this.addClass(setting);
        }
    }

    /**
     * Get the current darkmode setting observable
     */
    public darkmodeSetting() {
        return this.darkmodeSetting$;
    }

    private addClass(setting: 'dark' | 'light') {
        const containers = this.document.querySelectorAll('.ap'); // all
        containers.forEach((container) => {
            container.classList.remove('ap-light');
            container.classList.remove('ap-dark');
            container.classList.add(`ap-${setting}`);
        });

        // now also push the setting to the behavorSubject so that all subscribers of it get the correct mode:
        this.darkmodeSetting$.next(setting);
    }
}
